import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Brain, Zap, CheckCircle2, XCircle, MapPin, DollarSign, Briefcase } from "lucide-react";

interface ScoreBreakdown {
  skills: number;
  experience: number;
  location: number;
  salary: number;
}

interface ScoreRationale {
  skills?: {
    matched: number;
    total: number;
    matchedNames?: string[];
    missingNames?: string[];
  };
  experience?: {
    candidateYears: number;
    requiredYears: number;
  };
  location?: {
    candidateLocation: string;
    jobLocation: string;
    isMatch: boolean;
  };
  salary?: {
    candidateMin?: number;
    candidateMax?: number;
    jobMin?: number;
    jobMax?: number;
    currency?: string;
    isInRange: boolean;
  };
}

interface QuickScoreBadgeProps {
  score: number;
  breakdown?: ScoreBreakdown;
  rationale?: ScoreRationale;
  isAiScore?: boolean;
  aiReasoning?: string;
  className?: string;
}

export default function QuickScoreBadge({
  score,
  breakdown,
  rationale,
  isAiScore = false,
  aiReasoning,
  className = "",
}: QuickScoreBadgeProps) {
  const getScoreColor = (score: number) => {
    if (score >= 70) return "bg-green-500/20 text-green-700 border-green-500/30";
    if (score >= 50) return "bg-yellow-500/20 text-yellow-700 border-yellow-500/30";
    return "bg-red-500/20 text-red-700 border-red-500/30";
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`${getScoreColor(score)} font-semibold cursor-help ${className}`}
          >
            {isAiScore ? (
              <Brain className="w-3 h-3 mr-1" />
            ) : (
              <Zap className="w-3 h-3 mr-1" />
            )}
            {score}%
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="w-80 p-4" side="bottom" align="start">
          <div className="space-y-3">
            <div className="flex items-center gap-2 font-medium border-b pb-2">
              {isAiScore ? (
                <>
                  <Brain className="w-4 h-4 text-purple-500" />
                  <span>AI Score Breakdown</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span>Quick Score Breakdown</span>
                </>
              )}
              <span className="ml-auto text-lg font-bold">{score}%</span>
            </div>
            
            {breakdown && (
              <div className="space-y-3">
                <ScoreRowWithRationale 
                  label="Skills" 
                  value={breakdown.skills} 
                  weight={40}
                  icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                  rationale={rationale?.skills ? 
                    `${rationale.skills.matched}/${rationale.skills.total} required skills matched` : 
                    undefined
                  }
                  details={rationale?.skills?.matchedNames && rationale.skills.matchedNames.length > 0 ? (
                    <div className="mt-1 text-xs">
                      <div className="text-green-600 flex flex-wrap gap-1">
                        {rationale.skills.matchedNames.slice(0, 5).map((skill, i) => (
                          <span key={i} className="bg-green-100 px-1.5 py-0.5 rounded">{skill}</span>
                        ))}
                        {rationale.skills.matchedNames.length > 5 && (
                          <span className="text-muted-foreground">+{rationale.skills.matchedNames.length - 5} more</span>
                        )}
                      </div>
                      {rationale.skills.missingNames && rationale.skills.missingNames.length > 0 && (
                        <div className="text-red-600 flex flex-wrap gap-1 mt-1">
                          {rationale.skills.missingNames.slice(0, 3).map((skill, i) => (
                            <span key={i} className="bg-red-100 px-1.5 py-0.5 rounded line-through">{skill}</span>
                          ))}
                          {rationale.skills.missingNames.length > 3 && (
                            <span className="text-muted-foreground">+{rationale.skills.missingNames.length - 3} missing</span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : undefined}
                />
                <ScoreRowWithRationale 
                  label="Experience" 
                  value={breakdown.experience} 
                  weight={25}
                  icon={<Briefcase className="w-3.5 h-3.5" />}
                  rationale={rationale?.experience ? 
                    `${rationale.experience.candidateYears} years (${rationale.experience.requiredYears} required)` : 
                    undefined
                  }
                />
                <ScoreRowWithRationale 
                  label="Location" 
                  value={breakdown.location} 
                  weight={20}
                  icon={<MapPin className="w-3.5 h-3.5" />}
                  rationale={rationale?.location ? (
                    rationale.location.isMatch ? 
                      `✓ ${rationale.location.candidateLocation}` :
                      `${rationale.location.candidateLocation || 'Unknown'} ≠ ${rationale.location.jobLocation || 'Any'}`
                  ) : undefined}
                />
                <ScoreRowWithRationale 
                  label="Salary" 
                  value={breakdown.salary} 
                  weight={15}
                  icon={<DollarSign className="w-3.5 h-3.5" />}
                  rationale={rationale?.salary ? (
                    rationale.salary.isInRange ? 
                      `✓ Within budget` :
                      formatSalaryRationale(rationale.salary)
                  ) : undefined}
                />
              </div>
            )}

            {isAiScore && aiReasoning && (
              <div className="pt-2 border-t">
                <p className="text-xs font-medium mb-1">AI Reasoning:</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{aiReasoning}</p>
              </div>
            )}
            
            <div className="pt-2 border-t text-xs text-muted-foreground">
              {isAiScore 
                ? "AI-powered analysis based on detailed candidate profile"
                : "Algorithm-based score for quick filtering"
              }
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function formatSalaryRationale(salary: ScoreRationale['salary']): string {
  if (!salary) return '';
  const { candidateMin, candidateMax, jobMin, jobMax, currency = '' } = salary;
  const fmt = (v?: number) => v ? `${currency}${v.toLocaleString()}` : '?';
  return `Expects ${fmt(candidateMin)}-${fmt(candidateMax)} vs ${fmt(jobMin)}-${fmt(jobMax)}`;
}

interface ScoreRowWithRationaleProps {
  label: string;
  value: number;
  weight: number;
  icon: React.ReactNode;
  rationale?: string;
  details?: React.ReactNode;
}

function ScoreRowWithRationale({ label, value, weight, icon, rationale, details }: ScoreRowWithRationaleProps) {
  const getBarColor = (score: number) => {
    if (score >= 70) return "bg-green-500";
    if (score >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          {icon}
          <span>{label}</span>
          <span className="text-[10px]">({weight}%)</span>
        </div>
        <span className="font-semibold">{value}</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${getBarColor(value)} rounded-full transition-all`}
          style={{ width: `${value}%` }}
        />
      </div>
      {rationale && (
        <p className="text-[10px] text-muted-foreground">{rationale}</p>
      )}
      {details}
    </div>
  );
}
